import React, { useState, useEffect } from 'react';
import { formatPKR } from '../utils/currencyFormatter';
import './CompleteReturnModal.css';

const EnUr = ({ en, ur, as: Tag = 'span' }) => (
  <Tag className="en-ur">
    <span className="en-ur-en">{en}</span>
    {ur ? <span className="en-ur-ur" dir="rtl" lang="ur">{ur}</span> : null}
  </Tag>
);

const CompleteReturnModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  returnData,
  returnType = 'sale', // 'sale' or 'purchase'
  accounts = [], // list of { id, name, type } for cash/bank account selection
  isRegisteredCustomer = false // for sale returns: true = registered party (can credit to balance); false = walk-in (must refund from account)
}) => {
  const [refundAmount, setRefundAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [accountId, setAccountId] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentSplits, setPaymentSplits] = useState([]); // for sale return: [{ accountId: '', amount: '' }, ...]
  const [errors, setErrors] = useState({});

  const cashAccounts = accounts.filter(a => (a.type || a.account_type) === 'cash');
  const bankAccounts = accounts.filter(a => (a.type || a.account_type) === 'bank');
  const isSaleReturn = returnType === 'sale';
  const canCreditOnly = isSaleReturn && isRegisteredCustomer; // registered customer: default to credit (debt)
  const useSplits = isSaleReturn; // split refund across accounts for sale returns

  useEffect(() => {
    if (isOpen && returnData) {
      const total = parseFloat(returnData.total_amount) || 0;
      const deduction = parseFloat(returnData.meta?.refundDeduction ?? returnData.meta?.refund_deduction) || 0;
      const initialRefund = Math.max(0, total - deduction);
      setRefundAmount(initialRefund > 0 ? initialRefund.toString() : (total > 0 ? total.toString() : '0'));
      setPaymentMethod('cash');
      setReferenceNumber('');
      setErrors({});
      const list = accounts || [];
      const cash = list.filter(a => (a.type || a.account_type) === 'cash');
      const bank = list.filter(a => (a.type || a.account_type) === 'bank');
      if (useSplits) {
        if (canCreditOnly) {
          setPaymentSplits([{ accountId: '', amount: initialRefund > 0 ? String(initialRefund) : '' }]); // default: credit only
        } else {
          const defaultId = cash[0]?.id ?? bank[0]?.id;
          setPaymentSplits([{ accountId: defaultId != null ? String(defaultId) : '', amount: initialRefund > 0 ? String(initialRefund) : '' }]);
        }
      } else {
        if (returnType === 'purchase') {
          setAccountId('');
        } else {
          const defaultId = cash[0]?.id ?? bank[0]?.id;
          setAccountId(defaultId != null ? String(defaultId) : '');
        }
      }
    }
  }, [isOpen, returnData, accounts, returnType, canCreditOnly, useSplits]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = {};

    const amount = parseFloat(refundAmount);
    if (!refundAmount || isNaN(amount) || amount <= 0) {
      errors.refundAmount = 'Please enter a valid refund amount (greater than 0) / درست ریفنڈ رقم درج کریں (صفر سے زیادہ)';
    }

    const maxAllowed = Math.max(0, (parseFloat(returnData.total_amount) || 0) - (parseFloat(returnData.meta?.refundDeduction ?? returnData.meta?.refund_deduction) || 0));
    if (amount > maxAllowed) {
      errors.refundAmount = `Refund amount cannot exceed ${formatPKR(maxAllowed)} / ریفنڈ ${formatPKR(maxAllowed)} سے زیادہ نہیں ہو سکتا`;
    }

    if (useSplits) {
      const totalAllocated = paymentSplits.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
      if (Math.abs(totalAllocated - amount) > 0.01) {
        errors.paymentSplits = `Allocated total (${formatPKR(totalAllocated)}) must equal refund amount (${formatPKR(amount)}). / مختص کل (${formatPKR(totalAllocated)}) ریفنڈ رقم (${formatPKR(amount)}) کے برابر ہونی چاہیے۔`;
      }
      const hasAccountAllocation = paymentSplits.some(row => row.accountId && (parseFloat(row.amount) || 0) > 0);
      if (!canCreditOnly && !hasAccountAllocation) {
        errors.paymentSplits = errors.paymentSplits || 'Walk-in customers must refund from at least one account. Add a line with account and amount. / واک اِن گاہک کو کم از کم ایک اکاؤنٹ سے ریفنڈ دینا ہوگا۔';
      }
      if (paymentSplits.some(row => row.accountId && (parseFloat(row.amount) || 0) <= 0)) {
        errors.paymentSplits = errors.paymentSplits || 'Each line with an account selected must have an amount greater than 0. / اکاؤنٹ والی ہر لائن میں صفر سے زیادہ رقم ہونی چاہیے۔';
      }
    } else if (accounts.length > 0 && (!accountId || !accountId.trim())) {
      if (returnType === 'purchase') {
        // purchase: account optional
      } else if (!canCreditOnly) {
        errors.accountId = 'Walk-in customers must be refunded from an account. Please select a cash or bank account. / واک اِن گاہک کو اکاؤنٹ سے ریفنڈ دیں۔ نقد یا بینک اکاؤنٹ منتخب کریں۔';
      }
    }

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    if (useSplits) {
      const splits = paymentSplits
        .filter(row => (parseFloat(row.amount) || 0) > 0)
        .map(row => ({
          accountId: row.accountId ? parseInt(row.accountId, 10) : null,
          amount: parseFloat(row.amount)
        }));
      onConfirm({
        refundAmount: amount,
        paymentMethod,
        accountId: null,
        referenceNumber: referenceNumber || null,
        paymentSplits: splits
      });
    } else {
      onConfirm({
        refundAmount: amount,
        paymentMethod,
        accountId: accountId ? parseInt(accountId, 10) : null,
        referenceNumber: referenceNumber || null
      });
    }
  };

  if (!isOpen) return null;

  const isPurchaseReturn = returnType === 'purchase';
  const totalAmount = parseFloat(returnData?.total_amount) || 0;
  const deductionAmount = parseFloat(returnData?.meta?.refundDeduction ?? returnData?.meta?.refund_deduction) || 0;
  const maxRefund = Math.max(0, totalAmount - deductionAmount);

  const refundTotal = parseFloat(refundAmount) || 0;
  const allocatedTotal = paymentSplits.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const allocationMismatch = useSplits && Math.abs(allocatedTotal - refundTotal) > 0.01;

  return (
    <div className="complete-return-modal-overlay" onClick={onClose}>
      <div className="complete-return-modal" onClick={(e) => e.stopPropagation()}>
        <div className="complete-return-modal-header">
          <h3>
            <EnUr en="Complete Return" ur="واپسی مکمل کریں" />
          </h3>
          <button className="complete-return-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="complete-return-modal-content">
          <div className="return-info-card">
            <p>
              <strong><EnUr en="Return #:" ur="واپسی نمبر:" /></strong> {returnData.return_number}
            </p>
            <div className="complete-return-amounts">
              <p>
                <strong><EnUr en="Total amount:" ur="کل رقم:" /></strong> {formatPKR(returnData.total_amount)}
              </p>
              {(parseFloat(returnData.meta?.refundDeduction ?? returnData.meta?.refund_deduction) || 0) > 0 && (
                <>
                  <p>
                    <strong><EnUr en="Deducted amount:" ur="کٹی ہوئی رقم:" /></strong> {formatPKR(returnData.meta?.refundDeduction ?? returnData.meta?.refund_deduction)}
                  </p>
                  <p className="complete-return-refund-line">
                    <strong><EnUr en="Amount to refund:" ur="واپس کی جانے والی رقم:" /></strong> {formatPKR(Math.max(0, (parseFloat(returnData.total_amount) || 0) - (parseFloat(returnData.meta?.refundDeduction ?? returnData.meta?.refund_deduction) || 0)))}
                  </p>
                </>
              )}
            </div>
            {isPurchaseReturn ? (
              <p>
                <strong><EnUr en="Vendor:" ur="وینڈر:" /></strong> {returnData.vendor?.name || 'N/A'}
              </p>
            ) : (
              <p>
                <strong><EnUr en="Customer:" ur="گاہک:" /></strong> {returnData.customer?.name || returnData.customer_name || 'Walk-in / واک اِن'}
              </p>
            )}
            <div className="complete-return-payment-summary">
              <p className="complete-return-summary-title">
                {isPurchaseReturn ? (
                  <EnUr en="Amount credited to vendor" ur="وینڈر کو کریڈٹ کی جانے والی رقم" />
                ) : canCreditOnly ? (
                  <EnUr en="How to give back the refund" ur="ریفنڈ کیسے واپس کریں" />
                ) : (
                  <EnUr en="How to pay the refund" ur="ریفنڈ کیسے ادا کریں" />
                )}
              </p>
              <p className="complete-return-ledger-note">
                {isPurchaseReturn ? (
                  <EnUr
                    en="The return amount will be credited to the vendor (reduces your payable to them). Optionally select an account below if you are receiving this refund into cash or bank."
                    ur="واپسی کی رقم وینڈر کے کھاتے میں کریڈٹ ہو گی (آپ کی قابلِ ادائیگی کم ہو گی)۔ اگر نقد یا بینک میں وصول کر رہے ہیں تو نیچے اکاؤنٹ منتخب کریں۔"
                  />
                ) : canCreditOnly ? (
                  <EnUr
                    en="The amount you enter below is the total to give back to this customer. You can split it: one portion as credit to their balance, other portion(s) from available accounts — or the whole as credit, or the whole from one or more accounts."
                    ur="نیچے درج رقم گاہک کو واپس دینے والی کل رقم ہے۔ آپ اسے تقسیم کر سکتے ہیں: کچھ کریڈٹ بیلنس میں، کچھ اکاؤنٹس سے — یا ساری رقم کریڈٹ، یا ساری اکاؤنٹس سے۔"
                  />
                ) : (
                  <EnUr
                    en="Walk-in customers must be paid immediately. Pay from one or multiple accounts; the total allocated must equal the refund amount."
                    ur="واک اِن گاہک کو فوری ادائیگی کرنی ہوگی۔ ایک یا زیادہ اکاؤنٹس سے ادا کریں؛ کل رقم ریفنڈ کے برابر ہونی چاہیے۔"
                  />
                )}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="complete-return-form">
            <div className="complete-return-form-group">
              <label htmlFor="refundAmount" className="complete-return-form-label">
                {isPurchaseReturn ? (
                  <EnUr en="Amount to credit to vendor (PKR)" ur="وینڈر کو کریڈٹ کی جانے والی رقم (روپے)" />
                ) : (
                  <EnUr en="Total amount to give back to customer (PKR)" ur="گاہک کو واپس دینے والی کل رقم (روپے)" />
                )}{' '}
                <span className="required">*</span>
              </label>
              {isSaleReturn && (
                <p className="complete-return-form-hint complete-return-refund-amount-hint">
                  {canCreditOnly ? (
                    <EnUr
                      en="Total decided to refund for this sale return. Split this amount below as credit to their balance and/or payment from accounts."
                      ur="اس واپسی کے لیے طے شدہ کل ریفنڈ۔ نیچے اسے بیلنس کریڈٹ اور/یا اکاؤنٹس سے ادائیگی میں تقسیم کریں۔"
                    />
                  ) : (
                    <EnUr
                      en="Total to pay back for this sale return. Allocate it below across one or more accounts (walk-in must be paid immediately)."
                      ur="اس واپسی کی کل ادا کی جانے والی رقم۔ نیچے ایک یا زیادہ اکاؤنٹس میں تقسیم کریں (واک اِن کو فوری ادا کرنا ہوگا)۔"
                    />
                  )}
                </p>
              )}
              <div className="complete-return-input-wrapper">
                <span className="complete-return-currency">PKR</span>
                <input
                  id="refundAmount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={maxRefund || returnData.total_amount || 0}
                  className={`complete-return-form-input complete-return-form-input-with-currency ${errors.refundAmount ? 'error' : ''}`}
                  value={refundAmount}
                  onChange={(e) => {
                    setRefundAmount(e.target.value);
                    if (errors.refundAmount) {
                      setErrors({ ...errors, refundAmount: null });
                    }
                  }}
                  placeholder="0.00"
                  autoFocus
                />
              </div>
              {errors.refundAmount && (
                <span className="complete-return-form-error">{errors.refundAmount}</span>
              )}
            </div>

            {useSplits && accounts.length > 0 && (
              <div className="complete-return-form-group complete-return-splits-group">
                <label className="complete-return-form-label">
                  {canCreditOnly ? (
                    <EnUr en="Split the refund amount" ur="ریفنڈ رقم تقسیم کریں" />
                  ) : (
                    <EnUr en="Pay from account(s)" ur="اکاؤنٹ(س) سے ادا کریں" />
                  )}{' '}
                  <span className="required">*</span>
                </label>
                <p className="complete-return-form-hint">
                  {canCreditOnly ? (
                    <EnUr
                      en={'Split the amount to give back: use "Credit to customer only" for the portion added to their balance, or select accounts to pay from. You can do all credit, all from accounts, or a mix.'}
                      ur={'واپس دینے والی رقم تقسیم کریں: بیلنس والے حصے کے لیے "صرف گاہک کو کریڈٹ" رکھیں، یا اکاؤنٹس سے ادا کریں۔ سارا کریڈٹ، سارا اکاؤنٹس سے، یا دونوں ملا کر۔'}
                    />
                  ) : (
                    <EnUr
                      en="Walk-in must be paid immediately. Add lines and allocate amounts to one or more accounts. Total must equal the refund amount above."
                      ur="واک اِن کو فوری ادا کرنا ہوگا۔ لائنز شامل کر کے ایک یا زیادہ اکاؤنٹس میں رقم لگائیں۔ کل اوپر والی ریفنڈ رقم کے برابر ہونی چاہیے۔"
                    />
                  )}
                </p>
                <div className="complete-return-splits-list">
                  {paymentSplits.map((row, index) => {
                    const selectedInOtherRows = new Set(
                      paymentSplits.map((r, i) => (i !== index && r.accountId ? String(r.accountId) : null)).filter(Boolean)
                    );
                    const canShowAccount = (acc) =>
                      String(acc.id) === String(row.accountId) || !selectedInOtherRows.has(String(acc.id));
                    const cashFiltered = cashAccounts.filter(canShowAccount);
                    const bankFiltered = bankAccounts.filter(canShowAccount);
                    return (
                    <div key={index} className="complete-return-split-row">
                      <select
                        className="complete-return-form-input complete-return-split-account"
                        value={row.accountId}
                        onChange={(e) => {
                          const next = [...paymentSplits];
                          next[index] = { ...next[index], accountId: e.target.value };
                          setPaymentSplits(next);
                          if (errors.paymentSplits) setErrors({ ...errors, paymentSplits: null });
                        }}
                      >
                        <option value="">{canCreditOnly ? 'Credit to customer only / صرف گاہک کو کریڈٹ' : 'Select account / اکاؤنٹ منتخب کریں'}</option>
                        {cashFiltered.length > 0 && (
                          <optgroup label="Cash / نقد">
                            {cashFiltered.map(acc => {
                              const bal = acc.current_balance ?? acc.currentBalance ?? 0;
                              return (
                                <option key={acc.id} value={acc.id}>{acc.name} — {formatPKR(bal)}</option>
                              );
                            })}
                          </optgroup>
                        )}
                        {bankFiltered.length > 0 && (
                          <optgroup label="Bank / بینک">
                            {bankFiltered.map(acc => {
                              const bal = acc.current_balance ?? acc.currentBalance ?? 0;
                              const suffix = acc.bank_name ? ` (${acc.bank_name})` : '';
                              return (
                                <option key={acc.id} value={acc.id}>{acc.name}{suffix} — {formatPKR(bal)}</option>
                              );
                            })}
                          </optgroup>
                        )}
                      </select>
                      <div className="complete-return-split-amount-wrap">
                        <span className="complete-return-currency">PKR</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="complete-return-form-input complete-return-split-amount"
                          value={row.amount}
                          onChange={(e) => {
                            const next = [...paymentSplits];
                            next[index] = { ...next[index], amount: e.target.value };
                            setPaymentSplits(next);
                            if (errors.paymentSplits) setErrors({ ...errors, paymentSplits: null });
                          }}
                          placeholder="0"
                        />
                      </div>
                      <button
                        type="button"
                        className="complete-return-split-remove"
                        onClick={() => {
                          if (paymentSplits.length > 1) {
                            setPaymentSplits(paymentSplits.filter((_, i) => i !== index));
                            if (errors.paymentSplits) setErrors({ ...errors, paymentSplits: null });
                          }
                        }}
                        title="Remove line / لائن ہٹائیں"
                        aria-label="Remove line / لائن ہٹائیں"
                      >
                        ×
                      </button>
                    </div>
                  );
                  })}
                </div>
                <div className="complete-return-splits-actions">
                  <button
                    type="button"
                    className="complete-return-modal-button complete-return-modal-button-cancel complete-return-split-add"
                    onClick={() => setPaymentSplits([...paymentSplits, { accountId: '', amount: '' }])}
                  >
                    + Add line / لائن شامل کریں
                  </button>
                  <button
                    type="button"
                    className="complete-return-modal-button complete-return-modal-button-cancel complete-return-fill-remaining"
                    onClick={() => {
                      const total = parseFloat(refundAmount) || 0;
                      const allocated = paymentSplits.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
                      const remaining = Math.max(0, total - allocated);
                      if (remaining <= 0) return;
                      const next = [...paymentSplits];
                      const emptyIdx = next.findIndex(row => !row.amount || parseFloat(row.amount) === 0);
                      if (emptyIdx >= 0) {
                        next[emptyIdx] = { ...next[emptyIdx], amount: remaining.toFixed(2) };
                      } else {
                        next.push({ accountId: '', amount: remaining.toFixed(2) });
                      }
                      setPaymentSplits(next);
                      if (errors.paymentSplits) setErrors({ ...errors, paymentSplits: null });
                    }}
                  >
                    Fill remaining / باقی بھریں
                  </button>
                </div>
                <p className="complete-return-splits-total">
                  {canCreditOnly ? (
                    <EnUr en="Allocated for this return:" ur="اس واپسی کے لیے مختص:" />
                  ) : (
                    <EnUr en="Paid from accounts for this return:" ur="اس واپسی کے لیے اکاؤنٹس سے ادا:" />
                  )}{' '}
                  {formatPKR(paymentSplits.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0))} / {formatPKR(parseFloat(refundAmount) || 0)}
                </p>
                {errors.paymentSplits && (
                  <span className="complete-return-form-error">{errors.paymentSplits}</span>
                )}
              </div>
            )}

            {!useSplits && accounts.length > 0 && (
              <div className="complete-return-form-group">
                <label htmlFor="refundAccount" className="complete-return-form-label">
                  {returnType === 'purchase' ? (
                    <EnUr en="Account to receive refund into (optional)" ur="ریفنڈ وصولی کا اکاؤنٹ (اختیاری)" />
                  ) : canCreditOnly ? (
                    <EnUr en="Refund from account (optional)" ur="اکاؤنٹ سے ریفنڈ (اختیاری)" />
                  ) : (
                    <EnUr en="Refund from account" ur="اکاؤنٹ سے ریفنڈ" />
                  )}{' '}
                  {returnType !== 'purchase' && !canCreditOnly && <span className="required">*</span>}
                </label>
                <select
                  id="refundAccount"
                  className="complete-return-form-input"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  required={returnType !== 'purchase' && !canCreditOnly && accounts.length > 0}
                >
                  <option value="">
                    {returnType === 'purchase' ? 'Credit to vendor only (no account) / صرف وینڈر کو کریڈٹ' : canCreditOnly ? 'Credit to customer only (add to balance) / صرف گاہک کو کریڈٹ' : 'Select account (cash or bank) / اکاؤنٹ منتخب کریں'}
                  </option>
                  {cashAccounts.length > 0 && (
                    <optgroup label="Cash / نقد">
                      {cashAccounts.map(acc => {
                        const bal = acc.current_balance ?? acc.currentBalance ?? 0;
                        return (
                          <option key={acc.id} value={acc.id}>
                            {acc.name} — {formatPKR(bal)}
                          </option>
                        );
                      })}
                    </optgroup>
                  )}
                  {bankAccounts.length > 0 && (
                    <optgroup label="Bank / بینک">
                      {bankAccounts.map(acc => {
                        const bal = acc.current_balance ?? acc.currentBalance ?? 0;
                        const suffix = acc.bank_name ? ` (${acc.bank_name})` : '';
                        return (
                          <option key={acc.id} value={acc.id}>
                            {acc.name}{suffix} — {formatPKR(bal)}
                          </option>
                        );
                      })}
                    </optgroup>
                  )}
                </select>
                <p className="complete-return-form-hint">
                  {returnType === 'purchase' ? (
                    <EnUr
                      en={'If you receive the refund into cash or bank, select the account. Otherwise leave as "Credit to vendor only".'}
                      ur={'اگر نقد یا بینک میں ریفنڈ وصول کر رہے ہیں تو اکاؤنٹ منتخب کریں۔ ورنہ "صرف وینڈر کو کریڈٹ" رہنے دیں۔'}
                    />
                  ) : canCreditOnly ? (
                    <EnUr
                      en={'Leave as "Credit to customer only" to add to their balance, or select an account to pay the refund from.'}
                      ur={'بیلنس میں شامل کرنے کے لیے "صرف گاہک کو کریڈٹ" رکھیں، یا ریفنڈ ادا کرنے کے لیے اکاؤنٹ منتخب کریں۔'}
                    />
                  ) : (
                    <EnUr
                      en="The refund amount will be deducted from this account."
                      ur="ریفنڈ رقم اس اکاؤنٹ سے کاٹی جائے گی۔"
                    />
                  )}
                </p>
                {errors.accountId && (
                  <span className="complete-return-form-error">{errors.accountId}</span>
                )}
              </div>
            )}

            {(accountId || (useSplits && paymentSplits.some(r => r.accountId && (parseFloat(r.amount) || 0) > 0))) && (
              <>
                <div className="complete-return-form-group">
                  <label htmlFor="paymentMethod" className="complete-return-form-label">
                    <EnUr en="Payment Method" ur="ادائیگی کا طریقہ" /> <span className="required">*</span>
                  </label>
                  <select
                    id="paymentMethod"
                    className="complete-return-form-input"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    required
                  >
                    <option value="cash">Cash / نقد</option>
                    <option value="bank_transfer">Bank Transfer / بینک ٹرانسفر</option>
                    <option value="cheque">Cheque / چیک</option>
                    <option value="card">Card / کارڈ</option>
                    <option value="other">Other / دیگر</option>
                  </select>
                </div>

                <div className="complete-return-form-group">
                  <label htmlFor="referenceNumber" className="complete-return-form-label">
                    <EnUr en="Reference Number" ur="حوالہ نمبر" />
                  </label>
                  <input
                    id="referenceNumber"
                    type="text"
                    className="complete-return-form-input"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="e.g. Cheque #12345, Transaction ID / مثلاً چیک #12345"
                  />
                </div>
              </>
            )}

            <div className="complete-return-modal-actions">
              <button
                type="button"
                className="complete-return-modal-button complete-return-modal-button-cancel"
                onClick={onClose}
              >
                Cancel / منسوخ
              </button>
              <button
                type="submit"
                className="complete-return-modal-button complete-return-modal-button-confirm"
                disabled={allocationMismatch}
                title={allocationMismatch ? 'Allocated amount must equal the refund amount above / مختص رقم اوپر والی ریفنڈ کے برابر ہونی چاہیے' : ''}
              >
                Complete Return / واپسی مکمل کریں
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompleteReturnModal;
